/* eslint-disable react/react-in-jsx-scope */
import * as pdfjsLib from "pdfjs-dist";
import { useEffect, useRef, useState } from "react";

import IconPdf from "@/assets/icones/pdf.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import worker from "@/pdf-worker";
import { Download, Eye, Info, Link, MoreVertical, Trash2 } from "lucide-react";
import DocumentDetailsDialog from "./DocumentDetailsDialog";
pdfjsLib.GlobalWorkerOptions.workerPort = new worker();

type pdfThumbnailProps = {
  url: string;
  downloadUrl?: string;
  extension: string | undefined;
  treeFinancer: any;
  pj: any;
  otherpage?: boolean;
  canDelete?: boolean;
  onDelete?: (id: number) => void | Promise<void>;
  onShowDetails?: (doc: any) => void;
};

export const PdfThumbnail = ({
  url,
  downloadUrl,
  extension,
  treeFinancer,
  pj,
  otherpage = false,
  canDelete = false,
  onDelete,
  onShowDetails,
}: pdfThumbnailProps) => {
  const thumbRef = useRef<HTMLCanvasElement>(null);
  const fullContainerRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isThumbLoading, setIsThumbLoading] = useState(true);
  const handleCopyLink = async () => {
    try {
      await copyDocumentLink(url);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async () => {
    const downloadToast = showDownloadInProgressToast({
      filename: pj.name,
      kind: "file",
    });

    try {
      await downloadDocumentFile(downloadUrl ?? url, pj.name);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename: pj.name, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename: pj.name, kind: "file" });
    }
  };
  const handleShowDetails = () => {
    if (onShowDetails) {
      onShowDetails(pj);
      return;
    }

    setDetailsOpen(true);
  };
  // thumbnail
  useEffect(() => {
    const renderThumb = async () => {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.4 });
      const canvas = thumbRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d")!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
    };
    renderThumb().catch(console.error).finally(() => setIsThumbLoading(false));;
  }, [url]);

  // rendu complet
  useEffect(() => {
    if (!modalOpen) return;
    const renderAll = async () => {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const container = fullContainerRef.current;
      if (!container) return;
      container.innerHTML = "";
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const viewport = page.getViewport({ scale: 1.0 });
        const canvas = document.createElement("canvas");
        canvas.style.display = "block";
        canvas.style.margin = "auto";
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        container.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext("2d")!, viewport })
          .promise;
      }
    };
    renderAll().catch(console.error);
  }, [modalOpen, url]);

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogTrigger asChild>
        <div className={`bg-muted  ${!otherpage && "p-3"} rounded-md relative`}>
          <div className="flex justify-between items-center relative z-10 mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-3 "
                    style={{ width: "calc(100% - 45px)" }}
                  >
                    <IconPdf width={18} height={18} />
                    <h3 className="truncate flex-1">{pj.name}</h3>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col gap-2">
                  <div>
                    Porteur de projet :{" "}
                    <strong>
                      {treeFinancer?.find((t: any) => t.id === pj.pp)?.name}
                    </strong>
                  </div>
                  <div>
                    Programme :{" "}
                    <strong>
                      {
                        treeFinancer
                          ?.find((t: any) => t.id === pj.pp)
                          ?.programs.find((p: any) => p.id === pj.program)?.name
                      }
                    </strong>
                  </div>
                  <div>
                    Projet :{" "}
                    <strong>
                      {
                        treeFinancer
                          ?.find((t: any) => t.id === pj.pp)
                          ?.programs.find((p: any) => p.id === pj.program)
                          ?.projects.find((p: any) => p.id === pj.project)?.name
                      }
                    </strong>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!otherpage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="px-2 outline-none border-0 border-none ring-0 hover:bg-black/5 w-auto h-auto"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MoreVertical className="text-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="flex flex-col gap-2"
                  align="end"
                >
                  <DropdownMenuItem asChild>
                    <a
                      href={url}
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Eye width={22} height={22} /> Ouvrir
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleShowDetails();
                      }}
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Info width={22} height={22} /> Voir les détails
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDownload();
                      }}
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Download width={22} height={22} /> Télécharger
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCopyLink();
                      }}
                      className="flex gap-2 items-center cursor-pointer text-foreground"
                    >
                      <Link width={22} height={22} /> Copier le lien
                    </button>
                  </DropdownMenuItem>
                  {canDelete && (
                    <DropdownMenuItem asChild>
                      <button
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void onDelete?.(Number(pj.id));
                        }}
                        className="flex gap-2 items-center cursor-pointer text-red-500"
                      >
                        <Trash2 width={22} height={22} /> Supprimer
                      </button>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="relative h-[17rem] overflow-hidden rounded-md bg-white flex items-center justify-center">
            <div style={{ cursor: "pointer", display: "inline-block" }}>
              <canvas
                ref={thumbRef}
                style={{ borderRadius: 4, width: "248px", height: "272px" }}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="sm:max-w-4xl sm:w-full h-[90vh]">
        <DialogHeader>
          <DialogTitle>Aperçu complet du PDF</DialogTitle>
          <DialogDescription>Affichage de toutes les pages</DialogDescription>
        </DialogHeader>

        <div
          ref={fullContainerRef}
          style={{ overflow: "auto", maxHeight: "80vh" }}
        />

        <DialogClose asChild>
          <Button variant="outline" className="mt-4 w-max m-auto">
            Fermer
          </Button>
        </DialogClose>
      </DialogContent>
      <DocumentDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        pj={pj}
        treeFinancer={treeFinancer}
      />
    </Dialog>
  );
};

export default PdfThumbnail;
